import * as React from 'react'

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
  PermitFieldType,
} from '~/client/graph/types.generated'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { COLON } from '~/client/src/shared/utils/usefulStrings'

import DynamicFiltersListStore from '../DynamicFiltersList.store'
import FilterFieldSelector from './FieldSelector'
import FieldValuesSelector from './FieldValuesSelector'
import FilterOptionsList from './OptionsList'

interface IProps {
  filter: ITypeFieldFilterOptions
  selectedField: IPermitTypeField
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]
  isExpanded: boolean
  listStore: DynamicFiltersListStore

  isConfiguredFieldMaterialResource?: boolean
  configuredFieldType?: PermitFieldType
  isEditMode?: boolean
  isFormValid?: boolean
  isInactive?: boolean

  onToggle?: () => void
  onEditClick: () => void
  onRemove: () => void

  onFieldChange: (fieldId: string) => void
  onFieldValuesChange: (selectedFieldValues: string[]) => void
  onValuesChange: (selectedValues: string[]) => void
  onRemoveValueFromFilter?: (locationIds: Set<string>) => void
}

const ICON_SIZE = 18

export default observer(FilterRule)

function FilterRule({
  filter,
  selectedField,
  isConfiguredFieldMaterialResource,
  configuredFieldType,
  restrictedLocationIds,
  restrictedLocationTypes,
  isEditMode,
  isFormValid,
  isExpanded,
  isInactive,
  listStore,
  onToggle,
  onEditClick,
  onRemove,
  onFieldChange,
  onFieldValuesChange,
  onValuesChange,
  onRemoveValueFromFilter,
}: IProps) {
  const {
    filterFieldsOptions,
    selectedFieldRestrictedTypes,
    selectedFieldRestrictedLocIds,
    expandedFilterOptionsList,
  } = listStore
  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon
  const EditIcon = isEditMode ? CheckIcon : PencilIcon
  const RemoveIcon = isEditMode ? XIcon : Trash2Icon

  return (
    <div className="mb12 ba-palette-brand-lighter brada8 mw-fit-content">
      <div
        className={classList({
          'row y-center py5 px16 min-height40': true,
          'bb-palette-brand-lighter': isExpanded,
        })}
      >
        <ChevronIcon
          size={ICON_SIZE}
          className="mr10 text grey-30 pointer"
          onClick={onToggle}
        />

        <div className="row y-center text large wrap">
          <div className="my2 mr5 no-grow">{Localization.translator.if}</div>

          <FilterFieldSelector
            selectedField={selectedField}
            isInactive={isInactive}
            isEditMode={isEditMode}
            filterFieldsOptions={filterFieldsOptions}
            onChange={onFieldChange}
          />

          <div className="my2 mx5 no-grow nowrap text large lowercase grey-30">
            {Localization.translator.operatorValues.is}
          </div>

          <FieldValuesSelector
            filter={filter}
            selectedField={selectedField}
            isEditMode={isEditMode}
            restrictedLocationIds={selectedFieldRestrictedLocIds}
            restrictedLocationTypes={selectedFieldRestrictedTypes}
            onChange={onFieldValuesChange}
          />

          <div className="my2 mx5 no-grow nowrap text large grey-30">
            {Localization.translator.thenShow}
            {isExpanded && COLON}
          </div>

          {!isExpanded &&
            (filter.values.length
              ? Localization.translator.xOptions(filter.values.length)
              : Localization.translator.nothing)}
        </div>

        <div className="row y-center x-end no-grow ml10">
          <EditIcon
            size={16}
            className={classList({
              'text grey-30 mr10 no-grow pointer': true,
              'not-allowed': isEditMode && !isFormValid,
            })}
            onClick={onEditClick}
          />
          <RemoveIcon
            size={16}
            className="text grey-30 no-grow pointer"
            onClick={onRemove}
          />
        </div>
      </div>

      {isExpanded && (
        <FilterOptionsList
          filterOptions={expandedFilterOptionsList}
          isEditMode={isEditMode}
          restrictedLocationIds={restrictedLocationIds}
          restrictedLocationTypes={restrictedLocationTypes}
          isMaterialResourceField={isConfiguredFieldMaterialResource}
          configuredFieldType={configuredFieldType}
          onValuesChange={onValuesChange}
          onRemoveOption={onRemoveValueFromFilter}
        />
      )}
    </div>
  )
}
